import httpClient from './httpClient';

/**
 * Rekognition Service
 * Uses httpClient for automatic 401 handling and token refresh
 */

// Face Match API endpoint from environment variable
// Set by configure-api-endpoints.sh script at build time
// Example: https://eung13ysrl.execute-api.eu-west-2.amazonaws.com/QA
const FACE_MATCH_API_ENDPOINT = import.meta.env.VITE_FACE_MATCH_API;
const DETECT_FACE_ATTRIBUTES_API_ENDPOINT = import.meta.env.VITE_DETECT_FACE_ATTRIBUTES_API;

if (!FACE_MATCH_API_ENDPOINT) {
  console.error('VITE_FACE_MATCH_API not configured. Run: ./bin/frontend/configure-api-endpoints.sh <prefix> <env>');
}

if (!DETECT_FACE_ATTRIBUTES_API_ENDPOINT) {
  console.error('VITE_DETECT_FACE_ATTRIBUTES_API not configured. Run: ./bin/frontend/configure-api-endpoints.sh <prefix> <env>');
}

/**
 * Search for a matching face in tenant-specific AWS Rekognition collection
 * Tenant context (collection ID, captures bucket) is automatically provided by authorizer
 * JWT token in Authorization header determines which tenant collection to search
 * @param {string} imageDataUrl - Base64 encoded image data URL (optional if imageS3Key provided)
 * @param {number} faceMatchThreshold - Optional threshold (0-100) for face matching confidence
 * @param {boolean} multiFaceMode - Optional boolean for multi-face detection (false = single, true = multi)
 * @param {string} imageS3Key - Optional S3 key of uploaded image (alternative to base64)
 * @param {string} userId - Optional Cognito sub ID (required if using imageS3Key)
 * @param {Object} gpsCoords - Optional GPS coordinates {latitude, longitude, accuracy, timestamp}
 * @param {boolean} isIndoor - Optional indoor/outdoor flag (true = indoor, false = outdoor)
 * @param {string} mode - Optional processing mode: 'fast' (return known immediately, process unknowns async) or 'full' (process all synchronously)
 * @param {string} assignmentEventId - Optional open assignment event ID (for linking match events to assignments)
 * @param {string} assignmentDescription - Optional assignment description (for display in event UI)
 * @param {Object} clickPosition - Optional click position {x: percentage, y: percentage} where user clicked on image
 * @returns {Promise<Object>} Match result with face details, similarity score, and organized S3 key
 */
export async function searchFace(imageDataUrl, faceMatchThreshold, multiFaceMode, imageS3Key = null, userId = null, gpsCoords = null, isIndoor = false, mode = 'fast', assignmentEventId = null, assignmentDescription = null, clickPosition = null) {
  try {
    const body = {};

    // Use S3 key if provided, otherwise use base64
    if (imageS3Key) {
      body.imageS3Key = imageS3Key;
      body.userId = userId;
    } else if (imageDataUrl) {
      body.image = imageDataUrl;
      // Add userId for base64 mode too (needed for Lambda to save captured images)
      if (userId) {
        body.userId = userId;
      }
    } else {
      throw new Error('Either imageDataUrl or imageS3Key must be provided');
    }

    if (faceMatchThreshold !== undefined) {
      body.faceMatchThreshold = faceMatchThreshold;
    }
    if (multiFaceMode !== undefined) {
      body.multiFaceMode = multiFaceMode;
    }

    // Add GPS coordinates if provided (for outdoor proximity analysis)
    if (gpsCoords) {
      body.gpsCoords = gpsCoords;
    }

    // Add indoor/outdoor flag (default to false/outdoor if not specified)
    body.isIndoor = isIndoor !== undefined ? isIndoor : false;

    // Add processing mode (fast = return known immediately, full = process all synchronously)
    if (mode) {
      body.mode = mode;
    }

    // Add assignment event ID if provided (for linking match events to open assignments)
    if (assignmentEventId) {
      body.assignmentEventId = assignmentEventId;
    }

    // Add assignment description if provided (for display in event UI)
    if (assignmentDescription) {
      body.assignmentDescription = assignmentDescription;
    }

    // Add click position if provided (for analytics and future region-based processing)
    if (clickPosition) {
      body.clickPosition = clickPosition;
    }

    return await httpClient.post(`${FACE_MATCH_API_ENDPOINT}/match`, body);
  } catch (error) {
    console.error("Face match API call failed:", error);
    throw error;
  }
}

/**
 * Detect facial attributes from an image using AWS Rekognition
 * Stateless utility function with authentication and authorization (SEARCH_PERSONS permission)
 * @param {string} imageDataUrl - Base64 encoded image data URL
 * @returns {Promise<Object>} Detected facial attributes for all faces in image
 */
export async function detectFaceAttributes(imageDataUrl) {
  try {
    if (!imageDataUrl) {
      throw new Error('Image data is required');
    }

    return await httpClient.post(`${DETECT_FACE_ATTRIBUTES_API_ENDPOINT}/detectattributes`, {
      image: imageDataUrl
    });
  } catch (error) {
    console.error("Detect face attributes API call failed:", error);
    throw error;
  }
}
